import { useQueryClient } from "@tanstack/react-query";
import { createRootRoute, Outlet } from "@tanstack/react-router";
import { useEffect } from "react";
import { Toaster } from "sonner";

import { LoginScreen } from "@/features/auth/LoginScreen";
import { AppHeader } from "@/features/layout/AppHeader";
import { BottomNav } from "@/features/layout/nav";
import { NavigationProgress } from "@/features/layout/NavigationProgress";
import { authRequired } from "@/lib/auth/session";
import { useSession } from "@/lib/auth/use-session";
import { LiveEventsProvider } from "@/lib/events/live-events";

export const Route = createRootRoute({ component: Root });

// The auth gate wraps every route instead of redirecting to a /login route, so a deep link
// (`/pools/minswap`) survives the login and renders as soon as the key is accepted.
function Root() {
    const { key } = useSession();
    const queryClient = useQueryClient();
    const signedOut = key === null && authRequired();

    // Nothing fetched under one key may be shown under the next.
    useEffect(() => {
        if (signedOut) queryClient.clear();
    }, [signedOut, queryClient]);

    return (
        <>
            {signedOut ? (
                <LoginScreen />
            ) : (
                // Full width on purpose; the bottom padding keeps content clear of the phone tab bar.
                <LiveEventsProvider>
                    <NavigationProgress />
                    <div className="min-h-dvh pb-[calc(3.5rem+env(safe-area-inset-bottom))] md:pb-0">
                        <AppHeader />
                        <main className="px-3 py-4 sm:px-4 md:px-6 md:py-6 lg:px-8">
                            <Outlet />
                        </main>
                        <BottomNav />
                    </div>
                </LiveEventsProvider>
            )}
            {/* Toasts are feedback only (no actions), so taps pass through them: the operator copies
                several values in a row, and a toast over the next one used to swallow the tap. At
                the bottom, above the phone tab bar, they also leave the header readable. */}
            <Toaster
                theme="dark"
                position="bottom-center"
                mobileOffset={{ bottom: "calc(4rem + env(safe-area-inset-bottom))" }}
                toastOptions={{ className: "pointer-events-none!" }}
            />
        </>
    );
}
